import React, { useEffect, useMemo, useState } from "react";
import NumberFormat from "react-number-format";

import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  FormControlLabel,
  IconButton,
  Radio,
  Stack,
  TextField,
  TextFieldProps
} from "@mui/material";
import { DatePicker } from "components/Characteristics/Deal-form-drawer/DatePicker";
import { NumberInput } from "components/forms/Number-input/NumberInput";
import { debounce } from "lodash";
import { CharacteristicDataType } from "store/types";

import { currenciesArray } from "../../../../constants";
import { DataItem, UpdateItem } from "./SingeCharForm";

export const currencyCharVarName = "Currency";

interface Props {
  data: DataItem;
  characteristicName: string;
  data_type: CharacteristicDataType;
  filterCurrenciesOptions: () => string[];
  onRemove?: () => void;
  onUpdate: UpdateItem;
  viewOnly?: boolean;
}

const Inputs: Record<string, React.FC<TextFieldProps>> = {
  text: TextField,
  number: NumberInput,
  percentage: NumberInput,
  currency: NumberInput
};

export const MultipleDataItem: React.FC<Props> = ({
  data,
  characteristicName,
  data_type,
  filterCurrenciesOptions,
  onRemove,
  onUpdate,
  viewOnly
}) => {
  const { value, percentage, is_default } = data;

  const [localValue, setLocalValue] = useState<string>(value);
  const [localPercentage, setLocalPercentage] = useState<number>(percentage);

  const onUpdateDebounce = useMemo(() => debounce(onUpdate, 500), [onUpdate]);

  const handleUpdateValue = (value: string) => {
    setLocalValue(value);
    onUpdateDebounce({ value });
  };

  const handleUpdatePercentage = (percentage: number) => {
    setLocalPercentage(percentage);
    onUpdateDebounce({ percentage });
  };

  const handleUpdateDefault = (is_default: boolean) => {
    onUpdateDebounce({ is_default });
  };

  useEffect(() => {
    if (value === localValue) return;
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (percentage === localPercentage) return;
    setLocalPercentage(percentage);
  }, [percentage]);

  const Input = characteristicName !== currencyCharVarName ? Inputs[data_type] : null;
  const isCurrencyCharacteristic = characteristicName === currencyCharVarName;

  return (
    <Stack sx={{ flexDirection: "row", alignItems: "center", gap: "16px" }}>
      {isCurrencyCharacteristic && (
        <FormControlLabel
          control={
            <Radio
              sx={{ padding: "4px" }}
              checked={is_default || false}
              disabled={viewOnly}
              onChange={(e) => handleUpdateDefault(e.target.checked)}
              size="small"
            />
          }
          label="Display Currency"
          sx={{ margin: 0, "& .MuiFormControlLabel-label": { fontSize: "14px", whiteSpace: "nowrap" } }}
        />
      )}

      {Input && (
        <Input
          size="small"
          fullWidth
          disabled={viewOnly}
          value={localValue}
          onChange={(e) => handleUpdateValue(e.target.value)}
          {...(data_type === "percentage" && { numberInputProps: { suffix: "%", decimalScale: 2 } })}
        />
      )}
      {data_type === "currency" && characteristicName === currencyCharVarName && (
        <Autocomplete
          options={filterCurrenciesOptions()}
          value={localValue}
          fullWidth
          disabled={viewOnly}
          onChange={(_, option) => handleUpdateValue(option)}
          disableClearable
          renderInput={(params) => <TextField {...params} label="Currency" />}
        />
      )}

      {data_type === "currency" && characteristicName !== currencyCharVarName && (
        <Autocomplete
          options={currenciesArray}
          value={data.currency || ""}
          fullWidth
          disabled={viewOnly}
          onChange={(_, option) => onUpdate({ currency: option })}
          disableClearable
          renderInput={(params) => <TextField {...params} label="Currency" />}
        />
      )}

      {/* Generic date characteristic — full date picker (account format, stored as ISO midday). */}
      {data_type === "date" && (
        <DatePicker
          label={characteristicName}
          value={localValue}
          disabled={viewOnly}
          onChange={handleUpdateValue}
          onClear={() => handleUpdateValue("")}
        />
      )}
      <NumberFormat
        size="small"
        customInput={TextField}
        label="Share"
        disabled={viewOnly}
        sx={{ "& .MuiInputBase-root": { width: "85px" }, flexShrink: 0 }}
        value={localPercentage}
        onValueChange={({ value }) => handleUpdatePercentage(Number(value))}
        suffix="%"
        allowNegative={false}
        isNumericString
        decimalScale={2}
      />
      {onRemove && !viewOnly ? (
        <IconButton size="sm" sx={{ bgcolor: "#f5f6f7", flexShrink: 0 }} onClick={onRemove}>
          <CloseIcon />
        </IconButton>
      ) : (
        <Box sx={{ width: 24, flexShrink: 0 }} />
      )}
    </Stack>
  );
};
